import React, { useState, useEffect } from 'react';
import { Edit, Download, Plus, Trash, Search } from 'lucide-react';
import server from '../../../api/server';
import { FaTimes } from 'react-icons/fa';
import CreateKnowledgeBaseModal from './CreateKnowledgeBaseModal.jsx';
import DoctorScheduleModal, { downloadDirectory } from './DoctorScheduleModal.jsx';
import { useToast } from '../../../context/ToastContext.jsx';

const KnowledgeBasePage = ({ clientID }) => {
  const [showDoctorSchedule, setShowDoctorSchedule] = useState(false)
  const [loading, setLoading] = useState(false)
  const [knowledgeBaseData, setKnowledgeBaseData] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [client_id, setClient_id] = useState(null)
  const [email, setEmail] = useState(null)
  const [isEdit, setIsEdit] = useState(false)
  const [showCreateKnowledgeBaseModal, setShowCreateKnowledgeBaseModal] = useState(false)
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState(null)
  const [showConfirmBox, setShowConfirmBox] = useState(false)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [deleteBlocked, setDeleteBlocked] = useState(false)
  const [impactDetails, setImpactDetails] = useState(null)
  const { showToast } = useToast();
  const getKnowledgeBaseData = async () => {
    try {
      setLoading(true)
      let res = await server.getKnowledgeBaseData(client_id, email, 'voice-agents', 'knowledge-base')
      if(res.status === 200){
        let resp = await res.json()
        setKnowledgeBaseData(resp.data)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  const handleEditKnowledgeBase = (data) => {
    setIsEdit(true)
    setSelectedKnowledgeBase(data)
    if (data.resource_type === 'doctor_schedule') setShowDoctorSchedule(true)
    else setShowCreateKnowledgeBaseModal(true)
  }
  const handleAskDeleteKnowledgeBase = async (data) => {
    try{
      setSelectedKnowledgeBase(data)
      setShowConfirmBox(true)
      setConfirmLoading(true)
      setDeleteBlocked(false)
      setImpactDetails(null)
      let res = await server.knowledgeBaseDeleteImpact(client_id, email, 'voice-agents', 'knowledge-base', data.knowledge_base_id)
      let resp = await res.json()
      if(res.status === 200){
        setImpactDetails(resp.data)
        setDeleteBlocked(resp.data.can_delete === false)
      }else{
        showToast(resp.message || resp.error, 'error')
      }
    } catch (error) {
      setDeleteBlocked(true)
      console.log(error)
    } finally {
      setConfirmLoading(false)
    }
  }

  const handleConfirmDelete = async () => {
    if(!selectedKnowledgeBase || deleteBlocked) return;
    try{
      setLoading(true)
      let res = await server.deleteKnowledgeBase({client_id, email, menu: 'voice-agents', submenu: 'knowledge-base', 'knowledge_base_id': selectedKnowledgeBase.knowledge_base_id})
      let resp = await res.json()
      if(res.status === 200){
        showToast(resp.message, 'success')
        setShowConfirmBox(false)
        setSelectedKnowledgeBase(null)
        getKnowledgeBaseData()
      }else{
        showToast(resp.message || resp.error, 'error')
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadKnowledgeBase = async (data) => {
    try{
      if (data.resource_type === 'doctor_schedule') {
        const res = await server.getDoctorDirectory({client_id, email, menu: 'voice-agents', submenu: 'knowledge-base', doctor_directory_id: data.knowledge_base_id});
        const result = await res.json();
        if (!res.ok) throw new Error(result.message || result.error || 'Unable to export schedule');
        downloadDirectory(result.data.directory, data.display_name);
        return;
      }
      let res = await server.downloadKnowledgeBase({client_id, email, menu: 'voice-agents', submenu: 'knowledge-base', 'gs_uri': data.file_path})
      let resp = await res.json()
      if(res.status === 200){
        window.open(resp.url, '_blank')
      }else{
        showToast(resp.message || resp.error, 'error')
      }
    } catch (error) {
      showToast(error.message || 'Unable to download knowledge base', 'error')
    }
  }

  const handleCreateKnowledgeBase = () => {
    setIsEdit(false)
    setSelectedKnowledgeBase(null)
    setShowCreateKnowledgeBaseModal(true)
  }

  useEffect(() => {
    if(client_id && email){
      getKnowledgeBaseData()
    }
  }, [client_id, email])

  function getCookie(name) {
    function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
    var match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
    return match ? match[1] : null;
}

  useEffect(()=> {
    if(clientID){
      setClient_id(clientID)
    }
    const user = getCookie('dashboard_login_user_creds') ? JSON.parse(getCookie('dashboard_login_user_creds')) : null;
    if(user){
      setEmail(user.email)
    }
  }, [clientID])

  const query = searchQuery.trim().toLowerCase();
  const filteredKnowledgeBases = knowledgeBaseData.filter(data => [
    data.display_name, data.knowledge_base_id, data.description, data.updated_by,
    data.resource_type === 'doctor_schedule' ? 'Doctor Schedule' : 'Documents',
  ].some(value => (value || '').toLowerCase().includes(query)));

  return (
    <div className="py-3 px-5 bg-gray-50 font-sans h-full min-h-0 flex flex-col">
      <div className="mb-4 shrink-0">
        <h1 className="text-xl font-semibold text-gray-900">Knowledge Base</h1>
        <p className="text-sm text-gray-500 mt-2">Manage your AI agents knowledge and training data</p>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4 shrink-0">
        <div className="relative w-full sm:flex-1">
          <label htmlFor="knowledge-base-search" className="sr-only">Search knowledge bases</label>
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
          <input
            id="knowledge-base-search"
            type="search"
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
            placeholder="Search knowledge bases"
            className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
        <button onClick={() => {setSelectedKnowledgeBase(null); setShowDoctorSchedule(true);}} className="border border-indigo-600 text-indigo-600 px-3 py-1.5 rounded text-sm">
          <Plus className="w-4 h-4 inline mr-1" /> Add Doctor Schedule
        </button>
        <button onClick={handleCreateKnowledgeBase} className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded text-sm">
          <Plus className="w-4 h-4 inline mr-1" /> Add Documents
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm flex-1 min-h-0 overflow-auto">
          {
              loading ? (
                  <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
                      <div className="loader"></div>
                      <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                  </div>
              ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                      <thead style={{ position: "sticky", top: "0" }}>
                          <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{background: "white", fontSize: "13px"}}>Knowledge Base ID</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{background: "white", fontSize: "13px"}}>Name</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{background: "white", fontSize: "13px"}}>Type</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{background: "white", fontSize: "13px"}}>Description</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{background: "white", fontSize: "13px"}}>Searches</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{background: "white", fontSize: "13px"}}>Created By</th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{background: "white", fontSize: "13px"}}>Action</th>
                          </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                          {filteredKnowledgeBases.map((data) => (
                          <tr key={data.knowledge_base_id} className="hover:bg-gray-50">
                              <td className="px-5 py-3 whitespace-nowrap text-sm text-blue-600" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{data.knowledge_base_id}</td>
                              <td className="px-5 py-3 text-sm text-gray-900" style={{fontSize: "0.7rem", lineHeight: "1rem"}}><div className="w-60 whitespace-normal break-words">{data.display_name}</div></td>
                              <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-900" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{data.resource_type === 'doctor_schedule' ? 'Doctor Schedule' : 'Documents'}</td>
                              <td className="px-5 py-3 text-sm text-gray-900" style={{fontSize: "0.7rem", lineHeight: "1rem"}}><div className="w-60 whitespace-normal break-words line-clamp-2" title={data.description || ''}>{data.description}</div></td>
                              <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-500" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{data.resource_type === 'doctor_schedule' ? '—' : data.number_of_searches || 3}</td>
                              <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-500" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{data.updated_by}</td>
                              <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-500" style={{fontSize: "0.7rem", lineHeight: "1rem", cursor: "pointer", color: "blue"}}>
                                <div style={{display: "flex", gap: "10px"}}>
                                  <button onClick={()=>handleEditKnowledgeBase(data)} className="text-indigo-600 text-sm"><Edit size={16} /></button>
                                  <button onClick={()=>handleDownloadKnowledgeBase(data)} className="text-black-600 text-sm"><Download size={16} /></button>
                                  <button onClick={()=>handleAskDeleteKnowledgeBase(data)} className="text-red-600 text-sm"><Trash size={16} /></button>
                                </div>
                              </td>
                          </tr>
                          ))}
                          {!filteredKnowledgeBases.length && <tr><td colSpan={7} className="px-5 py-6 text-center text-sm text-gray-500">{query ? 'No knowledge bases match your search.' : 'No knowledge bases yet.'}</td></tr>}
                      </tbody>
                  </table>
              )
          }
      </div>
      {showDoctorSchedule && <DoctorScheduleModal clientID={client_id} emailID={email} selectedKnowledgeBase={selectedKnowledgeBase} onClose={() => setShowDoctorSchedule(false)} onSave={getKnowledgeBaseData} />}
      {showConfirmBox && (
        <div style={{zIndex: '9999999999', position: 'fixed', top: '0', left: '0', height: '100%', width: '100%', background: 'rgba(0,0,0, 0.5)'}}>
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md relative">
              <div className="flex justify-between items-center p-4 pb-2">
                <h2 className="text-xl text-gray-700" style={{fontWeight: '550'}}>Confirm Delete</h2>
                <button onClick={() => { setShowConfirmBox(false); setSelectedKnowledgeBase(null); }} className="text-gray-500 hover:text-gray-700"><FaTimes style={{color: 'slategray'}}/></button>
              </div>
              <div className="p-6 pt-2 text-sm text-gray-600">
                {confirmLoading && (
                  <div className="flex justify-center items-center" style={{ height: '12vh', flexDirection: 'column' }}>
                    <div className="loader"></div>
                    <div style={{textAlign: 'center'}}>Checking impact...</div>
                  </div>
                )}
                {!confirmLoading && (
                  <div>
                    {deleteBlocked ? (
                      <div>
                        <div className="text-red-600 mb-2">Deletion not allowed</div>
                        <div className="text-gray-700 text-sm">
                          {impactDetails?.message || 'This knowledge base is in use and cannot be deleted. Remove all agents using this knowledge base before deleting.'}
                          {typeof impactDetails?.impact_count === 'number' && impactDetails.impact_count > 0 && (
                            <div className="mt-1">Impacted: {impactDetails.impact_count}</div>
                          )}
                          {Array.isArray(impactDetails?.agents_in_use) && impactDetails.agents_in_use.length > 0 && (
                            <div className="mt-1">Agents: {impactDetails.agents_in_use.join(', ')}</div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div>Are you sure you want to delete <span className="font-medium">{selectedKnowledgeBase?.display_name}</span>?</div>
                        <div className="text-xs text-gray-500 mt-1">This action cannot be undone.</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3 p-6 pt-4">
                <button onClick={() => { setShowConfirmBox(false); setSelectedKnowledgeBase(null); }} className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50" style={{height: '40px', borderRadius: '4px'}}>Cancel</button>
                <button onClick={handleConfirmDelete} disabled={deleteBlocked || confirmLoading} className={`px-6 py-2 rounded-lg ${deleteBlocked || confirmLoading ? 'bg-gray-500 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'} text-white`} style={{height: '40px', borderRadius: '4px'}}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {
              showCreateKnowledgeBaseModal && (
                <div style={{position: "fixed", left: "0%", top: "0%", width: "100%", height: "100%", background: "rgba(0, 0, 0, 0.5)", zIndex: "1001"}}>
                        <div style={{position: "fixed", left: "25%", top: "2.5%", width: "50%", height: "95%", background: "white", borderRadius: "10px", boxShadow: "rgba(0, 0, 0, 0.05) 0px 1px 3px", 
                        zIndex: "999999999"}}>
                          <div className="flex items-center justify-between px-4 p-3">
                            <h2 className="text-lg font-medium text-gray-900">{isEdit ? 'Edit' : 'Add'} Documents</h2>
                            <div style={{position: "absolute", right: "15px", top: "15px", cursor: "pointer"}} onClick={()=>{setShowCreateKnowledgeBaseModal(false);}}><FaTimes style={{color: "slategray"}}/></div>
                          </div>
                            <div style={{overflow: "auto", height: "100%", borderRadius: "10px"}}>
                              <hr/>
                              <CreateKnowledgeBaseModal clientID={client_id} emailID={email} onClose={()=>setShowCreateKnowledgeBaseModal(false)} onSave={()=>{setShowCreateKnowledgeBaseModal(false); getKnowledgeBaseData();}} isEdit={isEdit} selectedKnowledgeBase={selectedKnowledgeBase} />
                            </div>
                        </div>
                    </div>
              )
            }

    </div>
  );
};

export default KnowledgeBasePage;
